import { Button, Col, Divider, message, Popconfirm, Row, Select, Space, Spin, Typography } from 'antd';
import { DeleteFilled, LoadingOutlined } from "@ant-design/icons";
import Table, { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { deletePlugin, deleteProductType, updateUserInfo } from 'services';
import { convertDateTime, fNumberToLocalString } from 'utils';

interface DataType {
  key: string | number;
  name: string;
  addedDate: string;
  product: string;
}

const formatFieldName: any = {
  totalPlusToken: 'Total sub Accounts (2 FREE)',
  plan: 'Subscription Type',
  productType: 'Plan type (BASIC = ADVANCED / HAZMAT = STARTER)',
  asinzenToken: '',
  promo: 'Promo',
  userHazType: 'Haz Type',
  registerFrom: 'Register From',
  createdAt: 'User signed up on',
  updatedAt: 'Account was last changed on DB',
  subscribedAt: 'User subscription was started on',
  nextSubscribeAt: 'User subscription will renew on',
  banAllAccess: "Has access to AZInsight",
  isCanceledAccount: 'User has disabled autorenew or the subsciption has expired',
  isOutOfTrial: 'Account used free trial',
};

const formatAsinzenTokenFieldName: any = {
  subscriptionInfo: 'asinzen-token subscription plan',
  remainingSubscriptionTokens: 'Available token of this subscription',
  onetimeInfo: 'one-time token purchase',
  freeToken: 'Free token',
}

const editableFields = ['totalPlusToken', 'plan', 'productType', 'userHazType', 'registerFrom', 'subscribedAt', 'nextSubscribeAt'];
const customValueFields = ['banAllAccess', 'isCanceledAccount', 'isOutOfTrial'];
const convertTimeFields = ['createdAt', 'updatedAt', 'subscribedAt', 'nextSubscribeAt'];

const PlansPluginsTab = ({ data, onReFetchUser }: any) => {
  const [dataSource, setDataSource] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fieldUpdate, setFieldUpdate] = useState<string>('');
  const [dataTable, setDataTable] = useState([]);

  const userId = data.user._id;

  const columns: ColumnsType<DataType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Added Date',
      dataIndex: 'addedDate',
      key: 'addedDate',
    },
    {
      title: 'Product',
      dataIndex: 'product',
      key: 'product',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_: string, record: any) => (
        <Popconfirm
          placement="topRight"
          title="Are you sure to delete this one?"
          onConfirm={() => handleOnDelete(record.key)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="primary" icon={<DeleteFilled />} danger />
        </Popconfirm>
      )
    },
  ];

  const handleOnDelete = (id: string) => {
    setLoading(true);
    deletePlugin(id)
      .then(() => {
        message.success('Deleted plugin successfully');
        setDataTable((prevState) => {
          const newData = prevState.filter((item: any) => item.key !== id);
          return newData;
        })
      })
      .catch(() => {
        message.error('Failed in delete plugin');
      })
      .finally(() => setLoading(false))
  }

  const handleOnChangeTextInline = (text: string, field: string) => {
    setFieldUpdate(field);
    setLoading(true);

    const params = {
      value: text,
      field,
      id: userId
    };
    updateUserInfo(params)
      .then(() => setDataSource((prevState: any) => {
        onReFetchUser(true)
        const newData = prevState.map((item: any) => {
          if (item.key === field) {
            return ({
              ...item,
              value: text
            })
          }
          return item;
        });
        return newData;
      }))
      .catch(() => {
        message.error('Cannot update field')
      })
      .finally(() => setLoading(false))
  };

  const handleOnDeleteProductType = (field: string) => {
    setFieldUpdate(field);
    setLoading(true);

    const userId = data.user._id;
    deleteProductType(userId)
      .then(() => setDataSource((prevState: any) => {
        const newData = prevState.map((item: any) => {
          if (item.key === field) {
            return ({
              ...item,
              value: 'None'
            })
          }
          return item;
        });
        return newData;
      }))
      .catch(() => {
        message.error('Cannot delete value')
      })
      .finally(() => setLoading(false))
  };


  const handleOnSelectField = (value: any, field: string) => {
    setFieldUpdate(field);
    setLoading(true);
    let valueUpdate = value ? '1' : '0';
    if (field === "banAllAccess") {
      valueUpdate = value ? '0' : '1';
    }
    const params = {
      value: valueUpdate,
      field,
      id: userId
    };
    updateUserInfo(params)
      .then(() => {
        setDataSource((prevState: any) => {
          const newData = prevState.map((item: any) => {
            onReFetchUser(true)
            if (item.key === field) {
              return ({
                ...item,
                value:
                  <Select
                    loading={fieldUpdate === field && loading}
                    onSelect={(value: any) => handleOnSelectField(value, field)}
                    value={field === "banAllAccess" ? !!value : value}
                    options={[
                      { label: 'True', value: true },
                      { label: 'False', value: false },
                    ]}
                  />
              })
            }
            return item;
          });
          return newData;
        })
        message.success("Updated successfully")
      })
      .catch((e) => {
        console.error(e)
        message.error("Cannot update this field")
      })
      .finally(() => setLoading(false))
  };

  const formatDataAZInsight = (source: any, tokens: any) => {
    setLoading(true);
    let profileData: any = [];

    //format for fields
    Object.keys(formatFieldName).forEach((field: string) => {
      if (field === "asinzenToken") {
        const fields = Object.keys(formatAsinzenTokenFieldName).map((f: any) => {
          return ({
            key: f,
            field: formatAsinzenTokenFieldName[f],
            value: isNaN(tokens[f]) ? tokens[f] : fNumberToLocalString(tokens[f]),
            editable: false,
            removable: false,
          })
        })
        profileData = [...profileData, ...fields];
      } else {
        const isEditable = editableFields.includes(field) ? true : false;
        const isRemovable = field === 'productType';
        const valueItem =
          customValueFields.includes(field) ? (
            <Select
              loading={fieldUpdate === field && loading}
              defaultValue={field === "banAllAccess" ? !source[field] : source[field]}
              onSelect={(value: any) => handleOnSelectField(value, field)}
              options={[
                { label: 'True', value: true },
                { label: 'False', value: false },
              ]}
            />
          ) : (
            source[field]
          );

        let item: any = {
          key: field,
          field: formatFieldName[field],
          value: valueItem || 'None',
          editable: isEditable ? {
            onChange: (text: string) => handleOnChangeTextInline(text, field)
          } : false,
          removable: isRemovable,
        };

        if (convertTimeFields.includes(field)) {
          item.value = convertDateTime(valueItem);
        }
        profileData.push(item);
      }
    });

    setDataSource(() => {
      setLoading(false);
      return profileData;
    });
  };

  const formatDataTable = (source: any) => {
    setLoading(true);
    const data = source.map((item: any) => {
      let createTime = "NONE";
      if (item.createdAt) {
        createTime = convertDateTime(item.createdAt);
      }
      return ({
        key: item._id,
        name: item.title,
        addedDate: createTime,
        product: item.parent,
      })
    })

    setDataTable(() => {
      setLoading(false);
      return data;
    });
  };

  useEffect(() => {
    formatDataAZInsight(data.user, data.asinzen_tokens);
    formatDataTable(data.plugins);
  }, [])

  return (
    <Spin spinning={loading} size="large">
      <Space direction="vertical" size="large" style={{ width: "100%", padding: 20 }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Divider orientation="left"><Typography.Title level={3}>AZInsight</Typography.Title></Divider>
          {dataSource.length > 0 && dataSource.map((data: any) => (
            <Row key={data.key} gutter={[16, 16]}>
              <Col span={6}>
                <Typography.Text strong>{data.field}:</Typography.Text>
              </Col>
              <Col>
                {fieldUpdate === data.key && loading ? (
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                ) : (
                  <>
                    <Typography.Text editable={data.editable}>
                      {data.value}
                    </Typography.Text>
                    {data.removable && <Button icon={<DeleteFilled />} danger style={{ marginLeft: 20 }} onClick={() => handleOnDeleteProductType('productType')} />}
                  </>
                )}
              </Col>
              <Divider style={{ margin: 0 }} />
            </Row>
          ))}
        </Space>
        <Space direction="vertical" size="large" style={{ width: "100%", marginTop: 30 }}>
          <Divider orientation="left"><Typography.Title level={3}>Products/Plugins</Typography.Title></Divider>
          <Table
            loading={loading}
            columns={columns}
            dataSource={dataTable}
            pagination={false}
          />
        </Space>
      </Space>
    </Spin>
  );
};

export default PlansPluginsTab;